import React, { useEffect, useState } from "react";
import AppSwitch from "../../../components/layout/app/AppSwitch";
import { TRAITS, hasTrait } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { useToggleTop } from "../../../components/common/ToggleTop";

// Tanks and motors per plumbing layout (waterFlowType), as in the app.
const TANKS = {
  TYPE_1: ["overHeadTank1", "underGroundTank"],
  TYPE_2: ["overHeadTank1", "underGroundTank"],
  TYPE_3: ["overHeadTank1", "overHeadTank2", "underGroundTank"],
  TYPE_4: ["overHeadTank1", "underGroundTank"],
  TYPE_5: ["overHeadTank2"],
  TYPE_6: ["overHeadTank1", "underGroundTank"],
};
const TANK_NAMES = { overHeadTank1: "OHT1", overHeadTank2: "OHT2", underGroundTank: "UGT" };
const TANK_LONG = { overHeadTank1: "Overhead tank 1", overHeadTank2: "Overhead tank 2", underGroundTank: "Underground tank" };
const LEVELS = { 0: { fill: "0%", word: "empty" }, 1: { fill: "20%", word: "low" }, 2: { fill: "80%", word: "high" } };

export const motorsOf = (device) => {
  if (device.waterFlowType === "TYPE_4") return [1];
  if (device.waterFlowType === "TYPE_5") return [2];
  return [1, 2];
};

// Tank levels and the pump motors of a water level controller (WLC_1).
const WaterLevelControl = ({ device, lastState, onChanged }) => {
  const toast = useToggleTop();
  const tanks = TANKS[device.waterFlowType] ?? TANKS.TYPE_1;
  const motors = motorsOf(device);
  const motorState = (motor) => lastState?.devices?.[`${device.applianceId}-${motor}`] ?? {};
  const [states, setStates] = useState({});

  useEffect(() => {
    setStates(Object.fromEntries(motors.map((motor) => [motor, motorState(motor).state === true])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastState]);

  const toggle = async (motor, next) => {
    setStates((current) => ({ ...current, [motor]: next }));
    try {
      await sendControl({
        on: [`${device.applianceId}`],
        off: [],
        action: { [device.applianceId]: { id: motor, trait: Number(TRAITS.MOTOR), value: next ? 1 : 0 } },
      });
      onChanged?.();
    } catch (err) {
      setStates((current) => ({ ...current, [motor]: !next }));
      toast(`Couldn't switch motor ${motor} ${next ? "on" : "off"}`, "red");
    }
  };

  return (
    <>
      <div className="hx-tanks">
        {tanks.map((tank) => {
          const level = LEVELS[lastState?.[tank]] ?? LEVELS[0];
          return (
            <div className="hx-tank" key={tank}>
              <div className="hx-tank-circle" role="img" aria-label={`${TANK_LONG[tank]}: ${level.word}`}>
                {level.fill !== "0%" ? <span className="hx-tank-water" style={{ "--level": level.fill }} /> : null}
                <span className="hx-tank-label" aria-hidden="true">
                  {TANK_NAMES[tank]}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {hasTrait(device, TRAITS.MOTOR) ? (
        <div className="hx-motors">
          {motors.map((motor) => {
            const info = motorState(motor);
            return (
              <div className="hx-motor" key={motor}>
                <div>
                  <div className="hx-motor-name" id={`hx-motor-${motor}`}>
                    Motor {motor}
                  </div>
                  <div className="hx-motor-meta">
                    {info.amps !== undefined ? `${Number(info.amps).toFixed(1)} A` : "No reading"}
                    {lastState?.volts ? ` · ${lastState.volts} V` : ""}
                  </div>
                </div>
                <AppSwitch
                  checked={states[motor]}
                  onChange={(next) => toggle(motor, next)}
                  label={`Motor ${motor}`}
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
};

export default WaterLevelControl;
